import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExcelJS from 'exceljs';

interface ExcelEditorProps {
  initialWorkbook: ExcelJS.Workbook;
  onSave: (workbook: ExcelJS.Workbook) => void;
  maxFileSize?: number;
  initialFileName?: string;
  // Note: maxFileSize and initialFileName are kept for API compatibility but not used internally
}

interface CellData {
  value: string | number | null;
  row: number;
  col: number;
  address: string;
}

export default function ExcelEditor({
  initialWorkbook,
  onSave,
}: ExcelEditorProps) {
  const [workbook, setWorkbook] = useState<ExcelJS.Workbook>(initialWorkbook);
  const [cellData, setCellData] = useState<Map<string, CellData>>(new Map());
  const [maxRow, setMaxRow] = useState(0);

  // Function to load cell data from workbook
  const loadCellData = (wb: ExcelJS.Workbook) => {
    const newCellData = new Map<string, CellData>();
    let maxR = 0;

    if (wb.worksheets.length > 0) {
      const worksheet = wb.worksheets[0];
      
      worksheet.eachRow((row, rowNumber) => {
        maxR = Math.max(maxR, rowNumber);
        row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
          const address = cell.address;
          const value = cell.value;
          
          let displayValue: string | number | null = null;
          if (value !== null && value !== undefined) {
            if (typeof value === 'object' && 'text' in value) {
              displayValue = (value as any).text || '';
            } else if (typeof value === 'object' && 'result' in value) {
              displayValue = (value as any).result || '';
            } else if (typeof value === 'boolean') {
              displayValue = value ? 'TRUE' : 'FALSE';
            } else if (value instanceof Date) {
              displayValue = value.toISOString();
            } else if (typeof value === 'string' || typeof value === 'number') {
              displayValue = value;
            } else {
              displayValue = String(value);
            }
          }
          
          newCellData.set(address, {
            value: displayValue,
            row: rowNumber,
            col: colNumber,
            address,
          });
        });
      });
    }

    setCellData(newCellData);
    setMaxRow(maxR);
  };

  // Update workbook when initialWorkbook prop changes
  useEffect(() => {
    setWorkbook(initialWorkbook);
  }, [initialWorkbook]);

  // Convert workbook to cell data
  useEffect(() => {
    loadCellData(workbook);
  }, [workbook]);

  // Handle cell value change
  const handleCellChange = (address: string, newValue: string) => {
    // Parse address to get row number
    const rowMatch = address.match(/\d+/);
    if (!rowMatch) return;
    const rowNumber = parseInt(rowMatch[0], 10);

    // Disable rows 1-4
    if (rowNumber >= 1 && rowNumber <= 4) {
      return;
    }

    // Parse address to get column number
    const colMatch = address.match(/^([A-Z]+)/);
    if (!colMatch) return;
    const colLetter = colMatch[1];
    let colNumber = 0;
    for (let i = 0; i < colLetter.length; i++) {
      colNumber = colNumber * 26 + (colLetter.charCodeAt(i) - 64);
    }

    // Get or create cell data
    let cell = cellData.get(address);
    if (!cell) {
      // Create new cell entry for empty cells
      cell = {
        value: null,
        row: rowNumber,
        col: colNumber,
        address,
      };
    }

    // Update cell data
    const updatedCellData = new Map(cellData);
    const updatedCell = { ...cell, value: newValue };
    updatedCellData.set(address, updatedCell);
    setCellData(updatedCellData);

    // Update workbook
    const worksheet = workbook.worksheets[0];
    if (worksheet) {
      const cellObj = worksheet.getCell(address);
      // Try to parse as number if possible, otherwise keep as string
      const numValue = Number(newValue);
      if (!isNaN(numValue) && newValue.trim() !== '') {
        cellObj.value = numValue;
      } else {
        cellObj.value = newValue;
      }
    }
  };

  // Handle row addition
  const handleAddRow = (rowNumber: number) => {
    // Only allow addition for rows 10-19
    if (rowNumber < 10 || rowNumber > 19) {
      return;
    }

    const worksheet = workbook.worksheets[0];
    if (!worksheet) return;

    // Insert a new empty row after the current row
    worksheet.spliceRows(rowNumber + 1, 0, []);

    // Update workbook state to trigger re-render
    setWorkbook(workbook);

    // Reload cell data to reflect the addition
    loadCellData(workbook);
  };

  // Handle row deletion
  const handleDeleteRow = (rowNumber: number) => {
    // Only allow deletion of rows 10-19
    if (rowNumber < 10 || rowNumber > 19) {
      return;
    }

    const worksheet = workbook.worksheets[0];
    if (!worksheet) return;

    // Delete the row from the worksheet
    worksheet.spliceRows(rowNumber, 1);

    // Reload cell data to reflect the deletion
    loadCellData(workbook);
  };

  // Handle download/save
  const handleDownload = async () => {
    try {
      onSave(workbook);
    } catch (error) {
      console.error('Error saving workbook:', error);
    }
  };

  // Generate column letters (A, B, C, ..., Z, AA, AB, ...)
  const getColumnLetter = (colNumber: number): string => {
    let result = '';
    while (colNumber > 0) {
      colNumber--;
      result = String.fromCharCode(65 + (colNumber % 26)) + result;
      colNumber = Math.floor(colNumber / 26);
    }
    return result;
  };

  // Get cell value for display
  const getCellValue = (row: number, col: number): string => {
    const colLetter = getColumnLetter(col);
    const address = `${colLetter}${row}`;
    const cell = cellData.get(address);
    if (!cell || cell.value === null || cell.value === undefined) {
      return '';
    }
    return String(cell.value);
  };

  // Check if row is disabled (rows 1-4)
  const isRowDisabled = (row: number): boolean => {
    return row >= 1 && row <= 4;
  };

  // Generate rows and columns
  const rows = useMemo(() => {
    const rowArray: number[] = [];
    for (let i = 1; i <= Math.max(maxRow, 50); i++) {
      rowArray.push(i);
    }
    return rowArray;
  }, [maxRow]);

  const columns = useMemo(() => {
    // Only display columns A-E (columns 1-5)
    return [1, 2, 3, 4, 5];
  }, []);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Excel Editor</Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Download
        </Button>
      </Box>
      
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <TableContainer component={Paper} sx={{ maxHeight: '100%' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: 'grey.200',
                    fontWeight: 'bold',
                    minWidth: 60,
                    width: 60,
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                  }}
                >
                  {/* Empty header for row numbers */}
                </TableCell>
                {columns.map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      backgroundColor: 'grey.200',
                      fontWeight: 'bold',
                      minWidth: 120,
                      textAlign: 'center',
                    }}
                  >
                    {getColumnLetter(col)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const disabled = isRowDisabled(row);
                return (
                  <TableRow
                    key={row}
                    sx={{
                      backgroundColor: disabled ? 'action.disabledBackground' : 'inherit',
                      '&:hover': disabled ? {} : { backgroundColor: 'action.hover' },
                    }}
                  >
                    <TableCell
                      sx={{
                        backgroundColor: disabled ? 'grey.300' : 'grey.100',
                        fontWeight: 'bold',
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        borderRight: 1,
                        borderColor: 'divider',
                        width: 120,
                        minWidth: 120,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                        <span>{row}</span>
                        {row >= 10 && row <= 19 && (
                          <>
                            <Tooltip title="Add row">
                              <IconButton
                                size="small"
                                onClick={() => handleAddRow(row)}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'primary.dark',
                                  },
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete row">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteRow(row)}
                                sx={{
                                  color: 'error.main',
                                  '&:hover': {
                                    backgroundColor: 'error.light',
                                    color: 'error.dark',
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    {columns.map((col) => {
                      const colLetter = getColumnLetter(col);
                      const address = `${colLetter}${row}`;
                      const value = getCellValue(row, col);
                      
                      return (
                        <TableCell
                          key={col}
                          sx={{
                            p: 0,
                            backgroundColor: disabled ? 'action.disabledBackground' : 'inherit',
                          }}
                        >
                          <TextField
                            fullWidth
                            value={value}
                            onChange={(e) => handleCellChange(address, e.target.value)}
                            disabled={disabled}
                            variant="outlined"
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                '& fieldset': {
                                  borderColor: 'transparent',
                                },
                                '&:hover fieldset': {
                                  borderColor: disabled ? 'transparent' : 'primary.main',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: disabled ? 'transparent' : 'primary.main',
                                },
                              },
                              '& .MuiInputBase-input': {
                                py: 0.5,
                                px: 1,
                                fontSize: '0.875rem',
                              },
                            }}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
